import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import mammoth from "mammoth";
import { toast } from "sonner";
import { AccountSchema, AgreementMemberInfo } from "../data/types";
import { USStatesEnum } from "../utils/enums";
import { stylePhoneNumber, styleSSN } from "../utils/helpers";
import { agreementMemberInfoSchema, getAgreementMemberInfoValidationError, getFirstAgreementMemberInfoInvalidField, type AgreementMemberInfoInvalidField } from "../utils/validationSchemas";
import AgreementSignatureSection from "./AgreementSignatureSection";

/** Agreement with at least a presigned URL (e.g. CommunityAgreementSchema from API). */
type MemberAgreementWithUrl = { url: string };

const PREVIEW_PROSE_CLASS =
  "prose prose-sm sm:prose max-w-none font-open-sans text-[#333] leading-relaxed [&_h1]:text-2xl [&_h1]:font-raleway [&_h1]:font-semibold [&_h2]:text-xl [&_h2]:font-raleway [&_h2]:font-semibold [&_h3]:text-lg [&_h3]:font-raleway [&_p]:my-2 [&_ul]:my-2 [&_ol]:my-2 [&_li]:my-0.5 [&_blockquote]:border-l-4 [&_blockquote]:border-primary-200 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-[#555] [&_table]:border-collapse [&_table]:w-full [&_th]:border [&_th]:border-[#ccc] [&_th]:p-2 [&_th]:bg-[#f5f5f5] [&_td]:border [&_td]:border-[#ccc] [&_td]:p-2 [&_img]:max-w-full [&_img]:h-auto [&_p:has(>img)]:text-center [&_p:has(>img)]:my-4 [&_.agreement-typewriter]:!font-['Courier_New',monospace] [&_.signatory-typewriter]:!font-['Courier_New',monospace] [&_.agreement-signature-block]:text-left [&_.agreement-signature-block]:block [&_.agreement-signature-block]:my-2 [&_.agreement-inline-input]:!font-['Courier_New',monospace] [&_.agreement-inline-input]:bg-transparent [&_.agreement-inline-input]:border-0 [&_.agreement-inline-input]:border-b [&_.agreement-inline-input]:border-[#555] [&_.agreement-inline-input]:outline-none [&_.agreement-inline-input]:px-0.5 [&_.agreement-inline-input]:py-0 [&_.agreement-inline-input]:min-w-[6em] [&_.agreement-inline-select]:!font-['Courier_New',monospace] [&_.agreement-inline-select]:bg-transparent [&_.agreement-inline-select]:border-0 [&_.agreement-inline-select]:border-b [&_.agreement-inline-select]:border-[#555] [&_.agreement-inline-select]:outline-none [&_.agreement-signature-trigger]:text-primary-200 [&_.agreement-signature-trigger]:underline [&_.agreement-signature-trigger]:text-sm [&_.agreement-signature-trigger]:cursor-pointer [&_.agreement-signature-trigger]:bg-transparent [&_.agreement-signature-trigger]:border-0 [&_.agreement-signature-trigger]:p-0 [&_.agreement-inline-input-invalid]:!border-red-500 [&_.agreement-inline-input-invalid]:bg-red-50 [&_.agreement-signature-trigger-invalid]:!text-red-600 [&_.agreement-signature-trigger-invalid]:font-semibold [&_.agreement-inline-input-invalid]:animate-pulse [&_.agreement-signature-trigger-invalid]:animate-pulse";

const PREVIEW_PROSE_SUBMIT_REVIEW_CLASS =
  `${PREVIEW_PROSE_CLASS} [&_.agreement-typewriter]:text-[#1a1a1a] [&_.agreement-typewriter]:font-medium [&_.agreement-signature-block]:rounded-lg [&_.agreement-signature-block]:border [&_.agreement-signature-block]:border-emerald-200 [&_.agreement-signature-block]:bg-emerald-50/40 [&_.agreement-signature-block]:px-3 [&_.agreement-signature-block]:py-2`;

const TYPEWRITER_SPAN = (val: string) =>
  `<span class="agreement-typewriter" style="font-family: 'Courier New', Courier, monospace;">${val.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")}</span>`;

/** Spacing between fullName and date, and between "Member Name" and "Date" in the label. */
const MEMBER_NAME_DATE_GAP = "&nbsp;&nbsp;&nbsp;&nbsp;";

const getTodayDateString = () => new Date().toISOString().slice(0, 10);

const escapeHtmlAttr = (val: string) =>
  val.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const INLINE_INPUT_STYLE =
  "font-family:'Courier New',Courier,monospace;background:transparent;border:none;border-bottom:1px solid #555;outline:none;min-width:6em;max-width:100%;padding:0 2px;";

const agreementInlineInput = (
  field: keyof AgreementMemberInfo | "email",
  value: string,
  opts?: { type?: string; placeholder?: string; readonly?: boolean; min?: string; max?: string }
) => {
  const type = opts?.type ?? "text";
  const readonly = opts?.readonly ? ' readonly="readonly"' : "";
  const placeholder = opts?.placeholder ? ` placeholder="${escapeHtmlAttr(opts.placeholder)}"` : "";
  const min = opts?.min ? ` min="${escapeHtmlAttr(opts.min)}"` : "";
  const max = opts?.max ? ` max="${escapeHtmlAttr(opts.max)}"` : "";
  return `<input type="${type}" class="agreement-inline-input agreement-typewriter" data-agreement-field="${field}" value="${escapeHtmlAttr(value)}" style="${INLINE_INPUT_STYLE}"${placeholder}${readonly}${min}${max} />`;
};

const agreementInlineSelect = (field: keyof AgreementMemberInfo, value: string) => {
  const options = Object.entries(USStatesEnum)
    .sort(([, a], [, b]) => a.fullName.localeCompare(b.fullName))
    .map(
      ([abbr, data]) =>
        `<option value="${escapeHtmlAttr(abbr)}"${abbr === value ? " selected" : ""}>${escapeHtmlAttr(abbr)}</option>`
    )
    .join("");
  return `<select class="agreement-inline-select agreement-typewriter" data-agreement-field="${field}" style="${INLINE_INPUT_STYLE}"><option value="">State</option>${options}</select>`;
};

const agreementSignatureBlock = (label: string, signatureDataUrl: string) => {
  const img = signatureDataUrl
    ? `<img src="${escapeHtmlAttr(signatureDataUrl)}" alt="Signature" class="max-w-[200px] max-h-[80px] my-2 block agreement-inline-signature" />`
    : "";
  const btnLabel = signatureDataUrl ? "Redraw signature" : "Draw signature";
  return `<span class="agreement-signature-block">${img}<button type="button" class="agreement-signature-trigger" data-agreement-signature-trigger>${btnLabel}</button><br/>${label}</span>`;
};

/** Replaces agreement template placeholders in HTML with memberInfo and authUser values; uses typewriter-style font for inserted values. */
function applyMemberInfoToHtml(
  html: string,
  info: AgreementMemberInfo | null | undefined,
  authUser: AccountSchema | null | undefined
): string {
  const i = info ?? {};
  const fullName = (i.fullName ?? "").trim();
  const companyName = (i.companyName ?? "").trim();
  const email = (authUser?.email ?? "").trim();
  const address = (i.address ?? "").trim();
  const city = (i.city ?? "").trim();
  const state = (i.state ?? "").trim();
  const zipcode = (i.zipcode ?? "").trim();
  const titlePosition = (i.titlePosition ?? "").trim();
  const ownerDriversLicense = (i.ownerDriversLicense ?? "").trim();
  const ownerSSN = (i.ownerSSN ?? "").trim();
  const ownerSSNDisplay = ownerSSN ? styleSSN(ownerSSN) : "";
  const memberFEIN = (i.memberFEIN ?? "").trim();
  const memberInsurance = (i.memberInsurance ?? "").trim();
  const foodSafetyCertDate = (i.foodSafetyCertDate ?? "").trim();
  const phoneRaw = (i.phone ?? authUser?.phone ?? "").trim();
  const phoneDisplay = phoneRaw ? stylePhoneNumber(phoneRaw) : "";
  const dateVal = (i.date ?? "").trim();
  const signatureDataUrl = (i.signatureDataUrl ?? "").trim();

  // Match straight (') or curly (') apostrophe in "Driver's" / "Member's"
  const ap = "['\u2019]?";
  let out = html;
  // Signatory Name and title: insert signature image right above when present
  if (signatureDataUrl) {
    const sigImg = `<img src="${signatureDataUrl.replace(/"/g, "&quot;")}" alt="Signature" class="max-w-[200px] max-h-[80px] my-2 block" />`;
    out = out.replace(
      /(Member\s+Signature\s*)/gi,
      (_, label) => `<span class="agreement-signature-block">${sigImg}<br/>${label}</span>`
    );
  }
  // Member Name Date: insert fullName and date (with sufficient gap) above label; same gap between "Member Name" and "Date"
  out = out.replace(
    /(Member\s+Name)\s+(Date)/gi,
    (_, memberName, date) => {
      const parts = [];
      if (fullName) parts.push(TYPEWRITER_SPAN(fullName));
      if (dateVal) parts.push(TYPEWRITER_SPAN(dateVal));
      const inline = parts.length ? parts.join(MEMBER_NAME_DATE_GAP) + "<br/>" : "";
      const labelWithGap = memberName + MEMBER_NAME_DATE_GAP + date;
      return inline + labelWithGap;
    }
  );
  // Member signature block: By / Name / Title (standalone "Name:" not "Owner(s) Name:")
  out = out.replace(/(By:\s*)_+\s*/gi, `$1${TYPEWRITER_SPAN(companyName)} `);
  out = out.replace(/(?<!Owner\(s\)\s)(Name:\s*)_+\s*/gi, `$1${TYPEWRITER_SPAN(fullName)} `);
  out = out.replace(/(Title:\s*)_+\s*/gi, `$1${TYPEWRITER_SPAN(titlePosition)} `);
  // Owner(s) Name: ___... → value in typewriter span
  out = out.replace(/(Owner\(s\)\s+Name:\s*)_+\s*/gi, `$1${TYPEWRITER_SPAN(fullName)} `);
  // Email: ___...
  out = out.replace(/(Email:\s*)_+\s*/gi, `$1${TYPEWRITER_SPAN(email)} `);
  // Phone: ___...
  out = out.replace(/(Phone:\s*)_+\s*/gi, `$1${TYPEWRITER_SPAN(phoneDisplay)} `);
  // Address/City/State/Postal Code: allow </p><p> or any content between parts (HTML has </p><p> between Address and City)
  out = out.replace(
    /(Address:\s*)_+([\s\S]*?)(City:\s*)_+([\s\S]*?)(State:\s*)_+([\s\S]*?)(Postal\s+Code:\s*)_+/gi,
    (_, a, sep1, c, sep2, e, sep3, postalLabel) =>
      `${a}${TYPEWRITER_SPAN(address)}${sep1}${c}${TYPEWRITER_SPAN(city)}${sep2}${e}${TYPEWRITER_SPAN(state)}${sep3}${postalLabel}${TYPEWRITER_SPAN(zipcode)}`
  );
  out = out.replace(new RegExp(`(Owner\\(s\\)\\s+Driver${ap}s?\\s+License:\\s*)_+\\s*`, "gi"), `$1${TYPEWRITER_SPAN(ownerDriversLicense)} `);
  out = out.replace(/(Owner\(s\)\s+SSN:\s*)_+\s*/gi, `$1${TYPEWRITER_SPAN(ownerSSNDisplay)} `);
  out = out.replace(/(Member\s+FEIN:\s*)_+\s*/gi, `$1${TYPEWRITER_SPAN(memberFEIN)} `);
  out = out.replace(new RegExp(`(Member${ap}s?\\s+Insurance:\\s*)_+\\s*`, "gi"), `$1${TYPEWRITER_SPAN(memberInsurance)} `);
  out = out.replace(new RegExp(`(Member${ap}s?\\s+Food\\s+Safety\\s+Certificate\\s+Date:\\s*)_+\\s*`, "gi"), `$1${TYPEWRITER_SPAN(foodSafetyCertDate)} `);
  // Title/Position if present in template
  out = out.replace(/(Title\/Position:\s*)_+\s*/gi, `$1${TYPEWRITER_SPAN(titlePosition)} `);
  return out;
}

/** Replaces agreement placeholders with inline editable inputs/selects for signing flow. */
export function applyEditableFieldsToHtml(
  html: string,
  info: AgreementMemberInfo | null | undefined,
  authUser: AccountSchema | null | undefined
): string {
  const i = info ?? {};
  const fullName = (i.fullName ?? "").trim();
  const companyName = (i.companyName ?? "").trim();
  const email = (authUser?.email ?? "").trim();
  const address = (i.address ?? "").trim();
  const city = (i.city ?? "").trim();
  const state = (i.state ?? "").trim();
  const zipcode = (i.zipcode ?? "").trim();
  const titlePosition = (i.titlePosition ?? "").trim();
  const ownerDriversLicense = (i.ownerDriversLicense ?? "").trim();
  const ownerSSN = (i.ownerSSN ?? "").trim();
  const memberFEIN = (i.memberFEIN ?? "").trim();
  const memberInsurance = (i.memberInsurance ?? "").trim();
  const foodSafetyCertDate = (i.foodSafetyCertDate ?? "").trim();
  const phoneRaw = (i.phone ?? authUser?.phone ?? "").trim();
  const dateVal = (i.date ?? "").trim() || getTodayDateString();
  const signatureDataUrl = (i.signatureDataUrl ?? "").trim();
  const todayStr = getTodayDateString();
  const ap = "['\u2019]?";
  let out = html;

  out = out.replace(/(Member\s+Signature\s*)/gi, (_, label) =>
    agreementSignatureBlock(label, signatureDataUrl)
  );

  out = out.replace(/(Member\s+Name)\s+(Date)/gi, (_, memberName, date) => {
    const fullNameInput = agreementInlineInput("fullName", fullName, { placeholder: "Full name" });
    const dateInput = agreementInlineInput("date", dateVal, { type: "date", min: todayStr, max: todayStr });
    return `${fullNameInput}${MEMBER_NAME_DATE_GAP}${dateInput}<br/>${memberName}${MEMBER_NAME_DATE_GAP}${date}`;
  });

  out = out.replace(/(By:\s*)_+\s*/gi, (_, label) =>
    `${label}${agreementInlineInput("companyName", companyName, { placeholder: "Business name" })} `
  );
  out = out.replace(/(?<!Owner\(s\)\s)(Name:\s*)_+\s*/gi, (_, label) =>
    `${label}${agreementInlineInput("fullName", fullName, { placeholder: "Full name" })} `
  );
  out = out.replace(/(Title:\s*)_+\s*/gi, (_, label) =>
    `${label}${agreementInlineInput("titlePosition", titlePosition, { placeholder: "Title" })} `
  );
  out = out.replace(/(Owner\(s\)\s+Name:\s*)_+\s*/gi, (_, label) =>
    `${label}${agreementInlineInput("fullName", fullName, { placeholder: "Full name" })} `
  );
  out = out.replace(/(Email:\s*)_+\s*/gi, (_, label) =>
    `${label}${agreementInlineInput("email", email, { readonly: true })} `
  );
  out = out.replace(/(Phone:\s*)_+\s*/gi, (_, label) =>
    `${label}${agreementInlineInput("phone", phoneRaw, { placeholder: "(555) 000-0000", type: "tel" })} `
  );
  out = out.replace(
    /(Address:\s*)_+([\s\S]*?)(City:\s*)_+([\s\S]*?)(State:\s*)_+([\s\S]*?)(Postal\s+Code:\s*)_+/gi,
    (_, a, sep1, c, sep2, e, sep3, postalLabel) =>
      `${a}${agreementInlineInput("address", address, { placeholder: "Street address" })}${sep1}${c}${agreementInlineInput("city", city, { placeholder: "City" })}${sep2}${e}${agreementInlineSelect("state", state)}${sep3}${postalLabel}${agreementInlineInput("zipcode", zipcode, { placeholder: "Zipcode" })}`
  );
  out = out.replace(
    new RegExp(`(Owner\\(s\\)\\s+Driver${ap}s?\\s+License:\\s*)_+\\s*`, "gi"),
    (_, label) =>
      `${label}${agreementInlineInput("ownerDriversLicense", ownerDriversLicense, { placeholder: "Driver's license" })} `
  );
  out = out.replace(/(Owner\(s\)\s+SSN:\s*)_+\s*/gi, (_, label) =>
    `${label}${agreementInlineInput("ownerSSN", ownerSSN, { placeholder: "111-22-3333", type: "tel" })} `
  );
  out = out.replace(/(Member\s+FEIN:\s*)_+\s*/gi, (_, label) =>
    `${label}${agreementInlineInput("memberFEIN", memberFEIN, { placeholder: "FEIN" })} `
  );
  out = out.replace(new RegExp(`(Member${ap}s?\\s+Insurance:\\s*)_+\\s*`, "gi"), (_, label) =>
    `${label}${agreementInlineInput("memberInsurance", memberInsurance, { placeholder: "Insurance" })} `
  );
  out = out.replace(
    new RegExp(`(Member${ap}s?\\s+Food\\s+Safety\\s+Certificate\\s+Date:\\s*)_+\\s*`, "gi"),
    (_, label) =>
      `${label}${agreementInlineInput("foodSafetyCertDate", foodSafetyCertDate, { type: "date", max: todayStr })} `
  );
  out = out.replace(/(Title\/Position:\s*)_+\s*/gi, (_, label) =>
    `${label}${agreementInlineInput("titlePosition", titlePosition, { placeholder: "Title" })} `
  );
  return out;
}

export { applyMemberInfoToHtml };

export type MembershipAgreementPreviewProps = {
  authUser: AccountSchema;
  memberAgreement: MemberAgreementWithUrl | null | undefined;
  memberInfo?: AgreementMemberInfo | null;
  setMemberInfo?: React.Dispatch<React.SetStateAction<AgreementMemberInfo>>;
  agreementLoading?: boolean;
  variant?: "desktop" | "mobile";
  /** Called whenever the preview HTML (with member info applied) is set or updated. Use to pass content into agreement submission. */
  onPreviewHtmlChange?: (html: string | null) => void;
  /** `edit`: inline fields in document. `submit-review`: read-only filled preview before submit. */
  interactionMode?: "edit" | "submit-review";
};

export type MembershipAgreementPreviewHandle = {
  validateMemberInfo: () => Promise<boolean>;
  highlightMissingFields: () => void;
  scrollToFirstInvalidField: () => void;
};

const scrollPreviewToInvalidField = (
  scrollContainer: HTMLElement | null,
  field: AgreementMemberInfoInvalidField | null
) => {
  if (!scrollContainer || !field) return;
  const target =
    field === "signature"
      ? scrollContainer.querySelector("[data-agreement-signature-trigger]")
      : scrollContainer.querySelector(`[data-agreement-field="${field}"]`);
  if (!target || !(target instanceof HTMLElement)) return;
  target.scrollIntoView({ behavior: "smooth", block: "center" });
  if (target.matches("input, select, button")) {
    target.focus({ preventScroll: true });
  }
};

const highlightInvalidAgreementFields = (
  container: HTMLElement | null,
  info: AgreementMemberInfo | null | undefined
) => {
  if (!container) return;
  container.querySelectorAll("[data-agreement-field]").forEach((el) => {
    el.classList.remove("agreement-inline-input-invalid");
  });
  container.querySelectorAll("[data-agreement-signature-trigger]").forEach((el) => {
    el.classList.remove("agreement-signature-trigger-invalid");
  });
  const parsed = agreementMemberInfoSchema.safeParse(info ?? {});
  if (!parsed.success) {
    const invalidFields = new Set(parsed.error.issues.map((issue) => String(issue.path[0])));
    invalidFields.forEach((field) => {
      container.querySelectorAll(`[data-agreement-field="${field}"]`).forEach((el) => {
        el.classList.add("agreement-inline-input-invalid");
      });
    });
  }
  if (!info?.signatureDataUrl?.trim().startsWith("data:")) {
    container.querySelectorAll("[data-agreement-signature-trigger]").forEach((el) => {
      el.classList.add("agreement-signature-trigger-invalid");
    });
  }
};

// /** Replaces DOL template placeholders with community name, address, and optional signatory info. */
// function applyMemberToDOLTemplate(
//   html: string,
//   community: CommunityForTemplate | null | undefined,
//   signatoryInfo?: AgreementSignatoryInfo | null
// ): string {
//   if (!community) return html;
//   // const name = (community.name ?? "").trim();
//   const fullAddress = formatCommunityAddress(community);
//   const streetOnly = (community.address ?? "").trim();
//   const cityStateZip = formatCityStateZip(community);
//   let out = html;
//   if (fullAddress) {
//     out = out.split(DOL_SAMPLE_FULL_ADDRESS).join(fullAddress);
//   }
//   if (streetOnly) {
//     out = out.split(DOL_SAMPLE_STREET).join(streetOnly ? `${streetOnly},` : "");
//   }
//   if (cityStateZip) {
//     out = out.split(DOL_SAMPLE_CITY_STATE_ZIP).join(cityStateZip);
//   }
//   if (community?.state) {
//     out = out.split("DC").join(Array.isArray(community.state) ? community.state[0] : community.state);
//     out = out.split("District of Columbia").join(Array.isArray(community.state) ? community.state[0] : community.state);
//     out = out.split("D.C.").join(Array.isArray(community.state) ? community.state[0] : community.state);
//   }

//   if (signatoryInfo?.adminSignatoryName) {
//     out = out.split("Christopher Bradshaw").join(signatoryInfo.adminSignatoryName);
//   } else {
//     out = out.replace(/\bChristopher Bradshaw\b/g, "[SIGNATORY NAME]");
//   }
//   if (signatoryInfo?.adminSignatoryJobTitle) {
//     out = out.split("Executive Director").join(signatoryInfo.adminSignatoryJobTitle);
//   } else {
//     out = out.replace(/\bExecutive Director\b/g, "[SIGNATORY JOB TITLE]");
//   }

//   if (signatoryInfo?.registeredBusinessName) {
//     out = out.split("Dreaming Out Loud").join(signatoryInfo.registeredBusinessName);
//     out = out.split("DOL Kitchen").join(`${signatoryInfo.registeredBusinessName} Kitchen`);
//     out = out.replace(/\bDOL\b/g, signatoryInfo.registeredBusinessName);
//     out = out.replace(/\bDol\b/g, signatoryInfo.registeredBusinessName);
    
//   } else {
//     out = out.split("Dreaming Out Loud").join("[BUSINESS NAME]");
//     out = out.split("DOL Kitchen").join(`[BUSINESS NAME] Kitchen`);
//     out = out.replace(/\bDOL\b/g, "[BUSINESS NAME]");
//     out = out.replace(/\bDol\b/g, "[BUSINESS NAME]");
//   }

//   if (signatoryInfo?.registeredBusinessEntity) {
//     out = out.split("Inc.").join(`${signatoryInfo.registeredBusinessEntity}.`);
//   } else {
//     out = out.split("Inc.").join("");
//   }

//   // Fix typo "15t5" -> "15th" (template or mammoth output); plain string and when "t5" is inside a tag e.g. 15<sup>t5</sup>
//   out = out.replace(/15t5/g, "15th");
//   out = out.replace(/15(<[^>]+>)t5(<\/[^>]+>)/g, "15$1th$2");
//   out = out.replace(/\b1301 Good Hope Road LLC\b/g, "Facility");
//   return out;
// }

/**
 * Fetches agreement from savedAgreement.url, converts to HTML if needed (e.g. docx via mammoth),
 * and renders a read-only, vertically scrollable preview with download-as-PDF and download-as-DOCX.
 */
const MembershipAgreementPreview = forwardRef<
  MembershipAgreementPreviewHandle,
  MembershipAgreementPreviewProps
>(({
  authUser,
  memberAgreement,
  memberInfo,
  setMemberInfo,
  agreementLoading = false,
  variant = "desktop",
  onPreviewHtmlChange,
  interactionMode = "edit",
}, ref) => {
  const [displayHtml, setDisplayHtml] = useState<string | null>(null);
  const rawHtmlRef = useRef<string>("");
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const editable = Boolean(setMemberInfo && memberInfo);
  const isSubmitReview = interactionMode === "submit-review";
  const showEditableDocument = editable && !isSubmitReview;

  useImperativeHandle(ref, () => ({
    validateMemberInfo: async () => {
      if (!memberInfo || !setMemberInfo) return true;
      const error = getAgreementMemberInfoValidationError(memberInfo);
      if (error) {
        toast.error(error);
        highlightInvalidAgreementFields(previewContainerRef.current, memberInfo);
        scrollPreviewToInvalidField(
          previewContainerRef.current,
          getFirstAgreementMemberInfoInvalidField(memberInfo)
        );
        return false;
      }
      highlightInvalidAgreementFields(previewContainerRef.current, memberInfo);
      return true;
    },
    highlightMissingFields: () => {
      highlightInvalidAgreementFields(previewContainerRef.current, memberInfo);
    },
    scrollToFirstInvalidField: () => {
      scrollPreviewToInvalidField(
        previewContainerRef.current,
        getFirstAgreementMemberInfoInvalidField(memberInfo)
      );
    },
  }));

  // Notify parent with filled (non-editable) HTML for agreement submission.
  useEffect(() => {
    if (!rawHtmlRef.current.length) {
      onPreviewHtmlChange?.(null);
      return;
    }
    onPreviewHtmlChange?.(applyMemberInfoToHtml(rawHtmlRef.current, memberInfo ?? undefined, authUser));
  }, [memberInfo, authUser, onPreviewHtmlChange, displayHtml]);

  const buildDisplayHtml = useCallback(
    (rawHtml: string) => {
      if (isSubmitReview || !editable) {
        return applyMemberInfoToHtml(rawHtml, memberInfo ?? undefined, authUser);
      }
      return applyEditableFieldsToHtml(rawHtml, memberInfo ?? undefined, authUser);
    },
    [editable, isSubmitReview, memberInfo, authUser]
  );

  const handleLoadMemberAgreement = useCallback(async () => {
    if (!memberAgreement?.url) {
      toast.error("No saved agreement found for this community.");
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const res = await fetch(memberAgreement.url);
      if (!res.ok) {
        throw new Error(
          res.status === 404
            ? "Agreement file not found. It may have been deleted or the link may have expired."
            : "Failed to download agreement file."
        );
      }
      const urlLower = memberAgreement.url.toLowerCase();
      const contentType = res.headers.get("content-type") ?? "";
      const isHtml =
        urlLower.includes(".html") ||
        urlLower.includes(".htm") ||
        contentType.includes("text/html");
      let html: string;
      if (isHtml) {
        html = await res.text();
      } else {
        const arrayBuffer = await res.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer });
        html = result.value;
      }
      rawHtmlRef.current = html;
      setDisplayHtml(buildDisplayHtml(html));
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Failed to load agreement.";
      setLoadError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [memberAgreement?.url, buildDisplayHtml]);

  // Load agreement automatically when we have a URL (no "Preview agreement" click needed).
  useEffect(() => {
    if (memberAgreement?.url && !displayHtml) {
      handleLoadMemberAgreement();
    }
  }, [memberAgreement?.url, handleLoadMemberAgreement, displayHtml]);

  // Read-only preview: re-render when memberInfo changes.
  useEffect(() => {
    if (!rawHtmlRef.current.length || showEditableDocument) return;
    setDisplayHtml(applyMemberInfoToHtml(rawHtmlRef.current, memberInfo ?? undefined, authUser));
  }, [showEditableDocument, memberInfo, authUser]);

  // Submit-review mode: show filled read-only document.
  useEffect(() => {
    if (!rawHtmlRef.current.length || !isSubmitReview) return;
    setDisplayHtml(applyMemberInfoToHtml(rawHtmlRef.current, memberInfo ?? undefined, authUser));
  }, [isSubmitReview, memberInfo, authUser]);

  // Editable preview: refresh inline signature block when signature image changes.
  useEffect(() => {
    if (!rawHtmlRef.current.length || !showEditableDocument) return;
    setDisplayHtml(applyEditableFieldsToHtml(rawHtmlRef.current, memberInfo ?? undefined, authUser));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- avoid re-render on every field keystroke
  }, [showEditableDocument, memberInfo?.signatureDataUrl]);

  // Returning from submit-review restores inline editable document.
  useEffect(() => {
    if (!rawHtmlRef.current.length || !showEditableDocument || isSubmitReview) return;
    setDisplayHtml(applyEditableFieldsToHtml(rawHtmlRef.current, memberInfo ?? undefined, authUser));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh only on mode switch, not every field edit
  }, [isSubmitReview, showEditableDocument]);

  // Sync inline field edits from the agreement document into memberInfo state.
  useEffect(() => {
    const container = previewContainerRef.current;
    if (!container || !showEditableDocument || !setMemberInfo) return;

    const syncFieldValue = (el: HTMLInputElement | HTMLSelectElement) => {
      const field = el.getAttribute("data-agreement-field");
      if (!field || field === "email") return;
      let value = el.value;
      if (field === "ownerSSN") {
        value = value.replace(/\D/g, "").slice(0, 9);
        el.value = styleSSN(value);
      }
      if (field === "phone") {
        const digits = value.replace(/\D/g, "");
        value =
          digits.length <= 10
            ? digits
            : digits.length === 11 && digits[0] === "1"
              ? "+1" + digits.slice(1)
              : digits.slice(0, 11);
      }
      setMemberInfo((prev) => ({
        ...prev,
        [field as keyof AgreementMemberInfo]: value,
        signatureDataUrl: prev.signatureDataUrl,
      }));
    };

    const onInput = (e: Event) => {
      const target = e.target as HTMLElement;
      if (!target.matches("[data-agreement-field]")) return;
      syncFieldValue(target as HTMLInputElement | HTMLSelectElement);
    };

    const onBlur = (e: Event) => {
      const target = e.target as HTMLElement;
      if (!target.matches('[data-agreement-field="phone"]')) return;
      const el = target as HTMLInputElement;
      const raw = el.value.replace(/\D/g, "").replace(/^1(?=\d{10}$)/, "");
      el.value = stylePhoneNumber(raw || el.value);
    };

    const onClick = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.closest("[data-agreement-signature-trigger]")) {
        e.preventDefault();
        setShowSignatureModal(true);
      }
    };

    container.addEventListener("input", onInput);
    container.addEventListener("change", onInput);
    container.addEventListener("blur", onBlur, true);
    container.addEventListener("click", onClick);
    return () => {
      container.removeEventListener("input", onInput);
      container.removeEventListener("change", onInput);
      container.removeEventListener("blur", onBlur, true);
      container.removeEventListener("click", onClick);
    };
  }, [displayHtml, showEditableDocument, setMemberInfo]);

  const isDesktop = variant === "desktop";

  if (agreementLoading) {
    return (
      <div className="flex flex-col w-full mt-3 p-4 border border-[#E9E9E9] rounded-lg bg-[#FAFAFA]">
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-2 border-primary-200 border-t-transparent rounded-full animate-spin" />
          <span className="ml-2 text-[14px] font-open-sans text-[#666]">
            Loading agreement…
          </span>
        </div>
      </div>
    );
  }

  if (!memberAgreement?.url) {
    return (
      <div className="flex flex-col w-full mt-3 p-4 border border-[#E9E9E9] rounded-lg bg-[#FAFAFA]">
        <p className="text-[14px] font-open-sans text-[#666] text-center">
          No agreement document is available for this community.
        </p>
      </div>
    );
  }

  if (loadError && !displayHtml) {
    return (
      <div className="flex flex-col w-full mt-3 p-4 border border-red-200 rounded-lg bg-red-50">
        <p className="text-[14px] font-open-sans text-red-700 mb-3">
          {loadError}
        </p>
        <button
          type="button"
          onClick={handleLoadMemberAgreement}
          disabled={loading}
          className="text-[14px] font-raleway font-medium text-primary-200 hover:underline disabled:opacity-60"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!displayHtml) {
    return (
      <div className="flex flex-col w-full mt-3 p-4 border border-[#E9E9E9] rounded-lg bg-[#FAFAFA]">
        <div className="flex items-center justify-center py-6">
          {loading ? (
            <>
              <div className="w-8 h-8 border-2 border-primary-200 border-t-transparent rounded-full animate-spin" />
              <span className="ml-2 text-[14px] font-open-sans text-[#666]">
                Loading agreement…
              </span>
            </>
          ) : (
            <button
              type="button"
              onClick={handleLoadMemberAgreement}
              className={`px-4 py-2.5 border border-[#E9E9E9] font-semibold text-[14px] font-raleway bg-white text-gray-600 hover:bg-gray-50 rounded-lg transition-colors ${
                isDesktop ? "mx-2" : ""
              }`}
            >
              Preview agreement
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full mt-3">
      {(editable && isSubmitReview) ? (
        <div
          data-testid={isSubmitReview ? "agreement-mode-banner-submit-review" : "agreement-mode-banner-edit"}
          className={
            isSubmitReview
              ? "mb-3 flex items-start gap-3 rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-white px-4 py-3 shadow-sm"
              : "mb-3 flex items-start gap-3 rounded-xl border border-dashed border-primary-200 bg-gradient-to-r from-primary-50/70 to-white px-4 py-3"
          }
        >
          <span
            aria-hidden
            className={
              isSubmitReview
                ? "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 text-[13px] font-bold"
                : "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primaryDark-200 text-[13px] font-bold"
            }
          >
            {isSubmitReview ? "✓" : "✎"}
          </span>
          <div className="min-w-0">
            <p
              className={
                isSubmitReview
                  ? "text-[15px] font-raleway font-semibold text-emerald-900"
                  : "text-[15px] font-raleway font-semibold text-primaryDark-200"
              }
            >
              {isSubmitReview ? "Review agreement before submitting" : "Edit mode — fill in the agreement"}
            </p>
            <p className="mt-0.5 text-[13px] font-open-sans text-[#555] leading-snug">
              {isSubmitReview
                ? "Confirm everything looks correct"
                : "Complete each field directly in the document below, then use Preview to see the final version."}
            </p>
          </div>
        </div>
      ) : null}
      <div
        ref={previewContainerRef}
        data-testid="agreement-preview-scroll-container"
        data-interaction-mode={interactionMode}
        className={`
          overflow-y-auto rounded-xl transition-colors duration-200
          ${isSubmitReview
            ? "border-2 border-solid border-emerald-300/90 bg-gradient-to-b from-emerald-50/30 to-white shadow-md ring-1 ring-emerald-100"
            : showEditableDocument
              ? "border-2 border-dashed border-primary-200/90 bg-gradient-to-b from-primary-50/25 to-white ring-1 ring-primary-100/80"
              : "border border-[#E9E9E9] bg-white shadow-sm"
          }
          ${isDesktop ? "max-h-[420px] min-h-[200px] px-6 py-5" : "max-h-[360px] min-h-[180px] px-4 py-4"}
        `}
        role="document"
        aria-label={
          isSubmitReview
            ? "Membership agreement preview — review before submit"
            : showEditableDocument
              ? "Membership agreement — edit mode"
              : "Membership agreement preview"
        }
      >
        <div
          className={isSubmitReview ? PREVIEW_PROSE_SUBMIT_REVIEW_CLASS : PREVIEW_PROSE_CLASS}
          dangerouslySetInnerHTML={{ __html: displayHtml }}
        />
      </div>
      {showEditableDocument && memberInfo && setMemberInfo ? (
        <AgreementSignatureSection
          memberInfo={memberInfo}
          setMemberInfo={setMemberInfo}
          variant={variant}
          hideTrigger
          modalOpen={showSignatureModal}
          onModalOpenChange={setShowSignatureModal}
        />
      ) : null}
    </div>
  );
});

MembershipAgreementPreview.displayName = "MembershipAgreementPreview";

export default MembershipAgreementPreview;
